import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from './api/axiosInstance.js';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import icEye from './assets/img/ic-eye.svg';
import icEyeClosed from './assets/img/ic-eye-closed.svg';

const PasswordResetToken = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [token, setToken] = useState('');

    const [pwd1, setPwd1] = useState('');
    const [pwd2, setPwd2] = useState('');

    const [showPwd1, setShowPwd1] = useState(false);
    const [showPwd2, setShowPwd2] = useState(false);

    const [pwdMsg, setPwdMsg] = useState('');
    const [pwdLoading, setPwdLoading] = useState(false);

    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    // ✅ same regex rules as backend
    const passwordRegex = useMemo(() => /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*\W).{8,}$/, []);

    const getPwdChecks = (pwd) => {
        const p = String(pwd || '');
        return {
            minLen: p.length >= 8,
            lower: /[a-z]/.test(p),
            upper: /[A-Z]/.test(p),
            number: /\d/.test(p),
            special: /\W/.test(p),
        };
    };

    const pwdChecks = useMemo(() => getPwdChecks(pwd1), [pwd1]);
    const pwdMatch = useMemo(() => {
        if (!pwd1 && !pwd2) return null;
        return pwd1 === pwd2;
    }, [pwd1, pwd2]);

    const validatePassword = (p1, p2) => {
        const a = String(p1 || '').trim();
        const b = String(p2 || '').trim();

        if (!a || !b) {
            setPwdMsg('Please enter and repeat your new password.');
            return false;
        }

        if (a !== b) {
            setPwdMsg('Passwords do not match.');
            return false;
        }

        if (!passwordRegex.test(a)) {
            setPwdMsg('Password must be at least 8 characters and include 1 uppercase, 1 lowercase, 1 number, and 1 special character.');
            return false;
        }

        return true;
    };

    useEffect(() => {
        const t = searchParams.get('token') || '';
        setToken(t);

        if (!t) {
            setError('Invalid reset link (missing token).');
        }
    }, [searchParams]);

    const submitPasswordReset = async () => {
        setPwdMsg('');
        setError('');
        setMessage('');

        if (!token) {
            setError('Invalid reset link (missing token).');
            return;
        }

        // ✅ strong password validation
        if (!validatePassword(pwd1, pwd2)) return;

        try {
            setPwdLoading(true);

            // ✅ send trimmed password
            await api.post('/api/auth/reset-password', {
                token,
                password: String(pwd1 || '').trim(),
            });

            setMessage('Password updated successfully. Redirecting to login...');

            // ✅ optional: cleanup
            setPwd1('');
            setPwd2('');
            setPwdMsg('');
            setShowPwd1(false);
            setShowPwd2(false);

            setTimeout(() => navigate('/'), 1200);
        } catch (err) {
            const msg =
                err?.response?.data?.msg ||
                err?.response?.data?.message ||
                'Reset failed. The link may be invalid or expired.';

            setError(msg);
        } finally {
            setPwdLoading(false);
        }
    };

    return (
        <main className="main-content flex-grow-1 ms-0 pt-xs-0">
            <Container>
                <Row>
                    <Col xl={4} className="offset-xl-4 pt-5">
                        <Card>
                            <Card.Body>
                                <h2 className="mb-2">Set a new password</h2>
                                <p className="mb-4">Choose a new password for your account.</p>

                                {error && <Alert variant="danger" className="m-0 mb-4 font-size-md">{error}</Alert>}
                                {message && <Alert variant="success" className="m-0 mb-4 font-size-md">{message}</Alert>}

                                <Form onSubmit={(e) => { e.preventDefault(); submitPasswordReset(); }}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>New password</Form.Label>

                                        <div style={{ position: 'relative' }}>
                                            <Form.Control
                                                type={showPwd1 ? 'text' : 'password'}
                                                value={pwd1}
                                                onChange={(e) => { setPwd1(e.target.value); setPwdMsg(''); }}
                                                autoComplete="new-password"
                                                placeholder="Enter password"
                                                style={{ paddingRight: '42px' }} // ✅ space for icon
                                                required
                                            />

                                            <img
                                                src={showPwd1 ? icEye : icEyeClosed}
                                                alt=""
                                                width={16}
                                                height={16}
                                                onClick={() => setShowPwd1(v => !v)}
                                                style={{
                                                    position: 'absolute',
                                                    right: '12px',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    cursor: 'pointer',
                                                    opacity: 0.7,
                                                }}
                                            />
                                        </div>
                                    </Form.Group>

                                    <Form.Group className="mb-2">
                                        <Form.Label>Repeat password</Form.Label>

                                        <div style={{ position: 'relative' }}>
                                            <Form.Control
                                                type={showPwd2 ? 'text' : 'password'}
                                                value={pwd2}
                                                onChange={(e) => { setPwd2(e.target.value); setPwdMsg(''); }}
                                                autoComplete="new-password"
                                                placeholder="Repeat password"
                                                style={{ paddingRight: '42px' }}
                                                required
                                            />

                                            <img
                                                src={showPwd2 ? icEye : icEyeClosed}
                                                alt=""
                                                width={16}
                                                height={16}
                                                onClick={() => setShowPwd2(v => !v)}
                                                style={{
                                                    position: 'absolute',
                                                    right: '12px',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    cursor: 'pointer',
                                                    opacity: 0.7,
                                                }}
                                            />
                                        </div>
                                    </Form.Group>

                                    <div className="mt-3">
                                        <div className="mb-2 font-size-sm">
                                            Create password that contains at least:
                                        </div>

                                        <div className="row g-2 font-size-sm">
                                            <div className="col-6">
                                                <div className={pwdChecks.minLen ? 'text-success' : 'text-danger'}>
                                                    {pwdChecks.minLen ? '✓' : '✕'} min. 8 characters
                                                </div>
                                            </div>

                                            <div className="col-6">
                                                <div className={pwdChecks.lower ? 'text-success' : 'text-danger'}>
                                                    {pwdChecks.lower ? '✓' : '✕'} one lowercase letter
                                                </div>
                                            </div>

                                            <div className="col-6">
                                                <div className={pwdChecks.upper ? 'text-success' : 'text-danger'}>
                                                    {pwdChecks.upper ? '✓' : '✕'} one capital letter
                                                </div>
                                            </div>

                                            <div className="col-6">
                                                <div className={pwdChecks.number ? 'text-success' : 'text-danger'}>
                                                    {pwdChecks.number ? '✓' : '✕'} one number
                                                </div>
                                            </div>

                                            <div className="col-12">
                                                <div className={pwdChecks.special ? 'text-success' : 'text-danger'}>
                                                    {pwdChecks.special ? '✓' : '✕'} one special character
                                                </div>
                                            </div>

                                            {pwdMatch !== null && (
                                                <div className="col-12">
                                                    <div className={pwdMatch ? 'text-success' : 'text-danger'}>
                                                        {pwdMatch ? '✓' : '✕'} passwords match
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {pwdMsg && (
                                        <div className="mt-3 font-size-sm text-danger">
                                            {pwdMsg}
                                        </div>
                                    )}

                                    <div className="d-flex justify-content-between mt-4">
                                        <Button type="button" variant="outline-secondary" onClick={() => navigate('/')}>
                                            Cancel
                                        </Button>

                                        <Button
                                            variant="primary"
                                            type="submit"
                                            disabled={!token || pwdLoading}
                                        >
                                            {pwdLoading ? 'Saving…' : 'Update password'}
                                        </Button>
                                    </div>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </main>
    );
};

export default PasswordResetToken;
